import { useTranslation } from 'react-i18next';
import { MAX_NOTE_FILES } from './note-files';
import {
  FilePickers,
  FileRow,
  FilesBox,
  PendingRows,
  RefusedRows,
  useSavedDetail,
} from './NoteFileParts';
import type { NoteFilesList, usePendingFiles } from './use-note-files';

/**
 * The note form's «Documenti della nota» (U1 board 1.1, 1.3): the files chosen wait for «Salva»,
 * as the saved files' ✕ does; a new note has no saved files yet.
 */
export function NoteFormFiles({
  saved,
  removals,
  onToggleRemoval,
  files,
}: {
  /** The note's saved files; absent for a note not created yet. */
  readonly saved: NoteFilesList | undefined;
  readonly removals: readonly string[];
  readonly onToggleRemoval: (documentId: string) => void;
  readonly files: ReturnType<typeof usePendingFiles>;
}) {
  const { t } = useTranslation('expenses-parity');
  const detail = useSavedDetail();
  if (saved !== undefined && saved.status !== 'ready') {
    return (
      <FilesBox count={0} hint={t('files.hint')}>
        <p
          role={saved.status === 'failed' ? 'alert' : 'status'}
          className="text-muted-foreground text-sm"
        >
          {t(saved.status === 'failed' ? 'files.loadFailed' : 'files.loading')}
        </p>
      </FilesBox>
    );
  }
  const kept = saved?.items.filter((file) => !removals.includes(file.documentId)) ?? [];
  const removed = saved?.items.filter((file) => removals.includes(file.documentId)) ?? [];
  const count = kept.length + files.pending.length;
  const canAdd = saved === undefined || saved.canAdd || removals.length > 0;
  return (
    <FilesBox count={count} hint={t('files.hint')}>
      {kept.map((file) => (
        <FileRow
          key={file.documentId}
          title={file.label ?? t('files.unnamed')}
          detail={detail(file)}
          onRemove={file.can.remove ? () => onToggleRemoval(file.documentId) : undefined}
        />
      ))}
      {removed.length > 0 ? (
        <p role="status" className="text-muted-foreground text-sm">
          {t('files.toRemove', { count: removed.length })}{' '}
          <button
            type="button"
            className="font-semibold underline"
            onClick={() => removed.forEach((file) => onToggleRemoval(file.documentId))}
          >
            {t('files.undoRemove')}
          </button>
        </p>
      ) : null}
      <PendingRows pending={files.pending} onLabel={files.label} onDrop={files.drop} />
      <RefusedRows refused={files.refused} onDismiss={files.dismiss} />
      {canAdd ? (
        <FilePickers
          full={count >= MAX_NOTE_FILES}
          busy={files.preparing}
          onFiles={(chosen) => void files.add(chosen)}
        />
      ) : (
        <p role="status" className="text-muted-foreground text-sm">
          {t('files.closed')}
        </p>
      )}
    </FilesBox>
  );
}
